import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Text, Content, FlexboxGrid, Form, Col, Stack } from 'rsuite'
import FlowPageLayout from '../../components/layout/FlowPageLayout'
import PortalHeader from '../../components/layout/PortalHeader'
import DocumentUpload from '../../components/form/DocumentUpload'
import Notice from '../../components/user-feedback/Notice'
import {
    constructMarkup,
    isNullUndefinedOrEmptyObject,
    objectHasProperty,
} from '../../common/utils'
import { setErrorMessage } from '../../store/slices/auth/authSlice'
import FormFieldErrorIcon from '../../components/form/FormFieldErrorIcon'
import { FLOWS_ENUM } from '../../common/enums'
import { updatePermitData } from '../../store/slices/adminUpdate/adminUpdateSlice'
import { selectConfigExpressApplyDocuments, selectConfigExpressRenewDocuments, selectConfigExpressUpdateDocuments } from '../../store/slices/config/configSelectors'
import { selectAdminUpdatedPermitData } from '../../store/slices/adminUpdate/adminUpdateSelectors'

const AdminUpdateDocuments = () => {
    const dispatch = useDispatch();
    const [hasError, setHasError] = useState(false);
    const [pageErrorMessage, setPageErrorMessage] = useState('');
    const [uploadValidationErrors, setUploadValidationErrors] = useState([]);
    const [fileLists, setFileLists] = useState({});

    const applyDocuments = useSelector(selectConfigExpressApplyDocuments) ?? [];
    const updateDocuments = useSelector(selectConfigExpressUpdateDocuments) ?? [];
    const renewDocuments = useSelector(selectConfigExpressRenewDocuments) ?? [];

    const selectedDocuments = useMemo(() => {
        return [applyDocuments, updateDocuments, renewDocuments].reduce((accumulator, currentArray) =>
            accumulator.concat(
                currentArray.filter(
                    (item) => !accumulator.some((accItem) => accItem.file_key === item.file_key)
                )
            ), []);
    }, [applyDocuments, updateDocuments, renewDocuments]);

    const { documents } = useSelector(selectAdminUpdatedPermitData);

    useEffect(() => {
        // Function dependencies
        const setFileListConfiguration = () => {
            let tempFileLists = { ...fileLists };

            selectedDocuments?.forEach((document) => {
                if (!objectHasProperty(document.file_key, fileLists)) {
                    tempFileLists[document.file_key] = {
                        required: document.required,
                        attachedFiles: documents?.[document.file_key]?.attachedFiles || [],
                    }
                }
            })
            setFileLists(tempFileLists);
        }

        // Effect logic
        setFileListConfiguration();
    }, [selectedDocuments, documents]);



    const onFileListChanged = (documentKey, files) => {
        clearUploadValidationError(documentKey);
        setHasError(false);
        setErrorMessage('');
        fileLists[documentKey].attachedFiles = files;
    };

    const clearUploadValidationError = (documentKey) => {
        let errors = { ...uploadValidationErrors };
        delete errors[documentKey];
        setUploadValidationErrors(errors);
    };
    const uploadDocuments = async () => {
        // In the admin update flow all documets are considered to be optional so no need to validate
        dispatchDocumentsToStore();
        return true;
    };

    const handleBackClick = () => {
        dispatchDocumentsToStore();
        return true;
    };

    const dispatchDocumentsToStore = () => {
        dispatch(
            updatePermitData({
                documents: fileLists,
            })
        );
    };

    return (
        <FlexboxGrid justify="space-between">
            <FlexboxGrid.Item colspan={24}>
                <PortalHeader flow={FLOWS_ENUM.ADMIN_UPDATE} showAccountDropdown={false} />
            </FlexboxGrid.Item>
            <FlexboxGrid.Item colspan={24}>
                <Content>
                    <FlowPageLayout
                        title="Upload Documents"
                        onNext={async () => {
                            return await uploadDocuments();
                        }}
                        onBack={handleBackClick}
                    >
                        <Form fluid>
                            <FlexboxGrid role="grid" className="mt-12 ml-0" xs={24} lg={12}>
                                <FlexboxGrid.Item role="row" colspan={24}>
                                    <Col>
                                        <Text className="paragraph">
                                            Supported formats: .jpg, .jpeg,
                                            .png, .pdf, .heic; Minimum file
                                            size: 15 KB; Maximum file size:
                                            4 MB; Maximum number of files
                                            per document type: 20.
                                        </Text>
                                    </Col>
                                </FlexboxGrid.Item>
                                <FlexboxGrid.Item
                                    colspan={24}
                                    className="mt-24"
                                    role="row"
                                    style={{ minHeight: '60px' }}
                                >
                                    <Col xs={24}>
                                        {!isNullUndefinedOrEmptyObject(
                                            fileLists
                                        ) &&
                                            selectedDocuments?.map(
                                                (document) => {
                                                    const documentFileList = fileLists[document.file_key];
                                                    return (
                                                        <FlexboxGrid
                                                            key={`document_upload_${document.file_key}`}
                                                            justify="start"
                                                            align="middle"
                                                            className="mb-24"
                                                        >
                                                            <FlexboxGrid.Item
                                                                colspan={24}
                                                            >
                                                                <Stack
                                                                    direction="row"
                                                                    spacing={
                                                                        4
                                                                    }
                                                                >
                                                                    <Text className="text-bold">
                                                                        {
                                                                            constructMarkup(document.title)
                                                                        }
                                                                    </Text>
                                                                    {uploadValidationErrors[
                                                                        document
                                                                            .file_key
                                                                    ] && (
                                                                            <FormFieldErrorIcon errorMessage="These documents are required." />
                                                                        )}
                                                                </Stack>
                                                            </FlexboxGrid.Item>
                                                            <FlexboxGrid.Item
                                                                colspan={24}
                                                            >
                                                                <Text>
                                                                    {
                                                                        constructMarkup(document.description)
                                                                    }
                                                                </Text>
                                                            </FlexboxGrid.Item>
                                                            <FlexboxGrid.Item
                                                                className="mt-8"
                                                                colspan={24}
                                                            >
                                                                <DocumentUpload
                                                                    ariaLabel={document.file_key}
                                                                    allowMultiple={true}
                                                                    fileList={documentFileList.attachedFiles}
                                                                    onChange={(documents) =>
                                                                        onFileListChanged(document.file_key, documents)
                                                                    }
                                                                    validExtensions={[
                                                                        '.jpg',
                                                                        '.jpeg',
                                                                        '.png',
                                                                        '.pdf',
                                                                        '.heic',
                                                                    ]}
                                                                    maxDocuments={20}
                                                                    onError={(msg) => {
                                                                        setHasError(msg && msg.length > 0);
                                                                        setPageErrorMessage(msg)
                                                                    }}
                                                                />
                                                            </FlexboxGrid.Item>
                                                        </FlexboxGrid>
                                                    )
                                                }
                                            )}
                                    </Col>
                                </FlexboxGrid.Item>
                                {hasError && (
                                    <FlexboxGrid.Item
                                        colspan={24}
                                        className="mt-24"
                                        role="row"
                                        style={{ minHeight: '60px' }}
                                    >
                                        <Col xs={24}>
                                            <Notice
                                                message={
                                                    pageErrorMessage?.length >
                                                        0
                                                        ? pageErrorMessage
                                                        : 'An error occurred while uploading documents.'
                                                }
                                            />
                                        </Col>
                                    </FlexboxGrid.Item>
                                )}
                            </FlexboxGrid>
                        </Form>
                    </FlowPageLayout>
                </Content>
            </FlexboxGrid.Item>
        </FlexboxGrid>
    )
}

export default AdminUpdateDocuments
